import { Helmet } from "react-helmet-async";

type SeoProps = {
  title: string;
  description: string;
  path: string;
  keywords?: string;
  noindex?: boolean;
  type?: "website" | "article";
  image?: string;
  structuredData?: Record<string, unknown> | Array<Record<string, unknown>>;
};

const SITE_NAME = "RunMark";
const SITE_URL = "https://runmark.app";
const DEFAULT_KEYWORDS =
  "production scheduling software, manufacturing scheduling, traceability labels, factory operations, run scheduling, manufacturing SaaS";

export default function Seo({
  title,
  description,
  path,
  keywords = DEFAULT_KEYWORDS,
  noindex = false,
  type = "website",
  image = "/social-preview.png",
  structuredData,
}: SeoProps) {
  const canonicalUrl = `${SITE_URL}${path}`;
  const socialImage = image.startsWith("http") ? image : `${SITE_URL}${image}`;
  const pageTitle = `${title} | ${SITE_NAME}`;
  const robots = noindex ? "noindex, nofollow" : "index, follow";
  const verificationToken = process.env.REACT_APP_GOOGLE_SITE_VERIFICATION?.trim();

  return (
    <Helmet>
      <title>{pageTitle}</title>
      <meta name="description" content={description} />
      <meta name="keywords" content={keywords} />
      <meta name="robots" content={robots} />
      <link rel="canonical" href={canonicalUrl} />

      <meta property="og:site_name" content={SITE_NAME} />
      <meta property="og:type" content={type} />
      <meta property="og:title" content={pageTitle} />
      <meta property="og:description" content={description} />
      <meta property="og:url" content={canonicalUrl} />
      <meta property="og:image" content={socialImage} />

      <meta name="twitter:card" content="summary_large_image" />
      <meta name="twitter:title" content={pageTitle} />
      <meta name="twitter:description" content={description} />
      <meta name="twitter:image" content={socialImage} />

      {verificationToken ? (
        <meta name="google-site-verification" content={verificationToken} />
      ) : null}

      {structuredData ? (
        <script type="application/ld+json">
          {JSON.stringify(structuredData)}
        </script>
      ) : null}
    </Helmet>
  );
}
